import {
  download,
  EllipsedText,
  FlexColumn,
  FlexRow,
  getFileNameFromPath,
  Loading,
  MaterialIcon,
  StyledButton,
  useLoading,
  useModal,
  useMoment,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { StyledDivider } from 'baseui/divider';
import { useEffect, useState } from 'react';
import { areEqual } from 'react-window';
import { useRecoilState, useRecoilValue } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../constants/app';
import { JOBS_STATUS_CODES } from '../../constants/jobs';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { cancelJob, generateJobPdfFile, getJob, getJobPdfFile, updateJobSchedule } from '../../services/jobsService';
import { jobScheduleErrorState, jobScheduleState, updateJobModalState } from '../../storage/jobStates';
import { Job } from '../../types/jobs';
import ConfirmModal from '../shared/ConfirmModal';
import StyledModal from '../shared/StyledModal';
import StyledTooltip from '../ui/StyledTooltip';
import { JobDetailForm } from './JobDetailForm';
import { JobDetailTabs } from './JobDetailTabs';
import { JobFailedVisitModal } from './JobFailedVisitModal';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  jobId: string;
};
export function JobModal({ isOpen, onClose, jobId }: Readonly<Props>) {
  const { css, theme } = useStyles();
  const [job, setJob] = useState<Job>();
  const { loading, startLoading, stopLoading } = useLoading();
  const { loading: loadingPdf, startLoading: startLoadingPdf, stopLoading: stopLoadingPdf } = useLoading();
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const { formatDateFromTs } = useMoment();
  const {
    showModal: showCancellationModal,
    openModal: openCancellationModal,
    closeModal: closeCancellationModal,
  } = useModal();
  const {
    showModal: showFailedVisitModal,
    openModal: openFailedVisitModal,
    closeModal: closeFailedVisitModal,
  } = useModal();
  const [update, setUpdate] = useRecoilState(updateJobModalState);
  const [editMode, setEditMode] = useState(false);
  const jobSchedule = useRecoilValue(jobScheduleState);
  const formHasError = useRecoilValue(jobScheduleErrorState);
  const canSubmit =
    !formHasError && job
      ? !areEqual(jobSchedule, {
          userId: job.user._id,
          scheduleDate: formatDateFromTs(job.scheduleDate, 'YYYY-MM-DDTHH:mm'),
        })
      : false;
  const jobStatusType = job?.status?.statusType;
  const jobIsClosed = jobStatusType === JOBS_STATUS_CODES['closed'];
  const jobIsOpen = jobStatusType === JOBS_STATUS_CODES['open'];
  const jobIsClosedOrFailedVisit = jobIsClosed || jobStatusType === JOBS_STATUS_CODES['closed_with_failed_visit'];

  useEffect(() => {
    if (isOpen) {
      return clearStates();
    }
  }, [isOpen]);

  const clearStates = () => {
    setJob(undefined);
  };

  const loadJob = async () => {
    startLoading();
    const { job, error } = await getJob(jobId);
    if (!error && job) {
      setJob(job);
    } else {
      showFailFeedback(error || FEEDBACK.failedFetch(FEEDBACK_PREFIXES.job));
      onCloseModal();
    }
    stopLoading();
  };

  useEffect(() => {
    if (!jobId) {
      onCloseModal();
      return;
    }
    loadJob();
  }, [jobId]);

  useEffect(() => {
    if (jobId && update) {
      loadJob();
      setEditMode(false);
    }
    setUpdate(undefined);
  }, [jobId, update]);

  const onCloseModal = () => {
    clearStates();
    onClose();
    setEditMode(false);
  };

  const downloadFile = async () => {
    if (job?.pdf) {
      const { url } = await getJobPdfFile(jobId);
      if (url) {
        const filename = getFileNameFromPath(url);
        download(url, filename);
      }
    }
  };

  const generateFile = async () => {
    startLoadingPdf();
    const { msg, error } = await generateJobPdfFile(jobId);
    if (!error && msg) {
      showPositiveFeedback(msg);
    } else {
      showFailFeedback(error || 'Ocurrió un error al generar el PDF.');
    }
    stopLoadingPdf();
  };

  const onRescheduleSubmit = async () => {
    startLoading();
    const { error } = await updateJobSchedule(jobId, jobSchedule);
    if (!error) {
      setUpdate('update');
      showPositiveFeedback(FEEDBACK.edited(FEEDBACK_PREFIXES.jobSchedule, job?.name));
    } else {
      showFailFeedback(error);
    }
    setEditMode(false);
    stopLoading();
  };

  const onCancelJob = async () => {
    const { error } = await cancelJob(jobId);
    if (!error) {
      showPositiveFeedback(`${FEEDBACK_PREFIXES.job} se canceló correctamente.`);
      closeCancellationModal();
      setUpdate('update');
    } else {
      showFailFeedback(
        `Ocurrió un error al editar ${FEEDBACK_PREFIXES.job.toLowerCase()} ${job?.name}, inténtalo nuevamente.`,
      );
    }
  };

  return (
    <StyledModal
      isOpen={isOpen}
      closeable
      onClose={onCloseModal}
      size="auto"
      title={job?.name}
      content={
        <FlexColumn
          classNames={css({
            justifyContent: 'space-between',
            overflow: 'hidden',
            minWidth: '700px',
            maxWidth: '1000px',
            width: '50vw',
          })}
        >
          <FlexColumn
            classNames={css({
              overflow: 'auto',
              height: '100%',
              minHeight: '35vh',
            })}
          >
            {loading && !job && (
              <FlexColumn
                centered
                classNames={css({ flex: 1 })}
              >
                <Loading />
              </FlexColumn>
            )}
            {!loading && job && (
              <>
                {!editMode && <JobDetailTabs job={job} />}
                {editMode && <JobDetailForm job={job} />}
              </>
            )}
          </FlexColumn>
          <FlexColumn gap={theme.sizing.scale200}>
            <StyledDivider
              $size="cell"
              style={{ marginTop: 0, marginBottom: 0, width: '100%' }}
            />
            <FlexRow
              classNames={css({ justifyContent: 'end' })}
              gap={theme.sizing.scale300}
            >
              {jobIsClosed && (
                <StyledTooltip
                  content="Re-generar PDF"
                  showArrow={false}
                >
                  <Button
                    size="mini"
                    shape="square"
                    onClick={generateFile}
                    kind="tertiary"
                    isLoading={loadingPdf}
                  >
                    <MaterialIcon name="downloading" />
                  </Button>
                </StyledTooltip>
              )}
              {!jobIsClosedOrFailedVisit && (
                <StyledButton
                  size="mini"
                  onClick={openFailedVisitModal}
                  isLoading={showFailedVisitModal}
                >
                  <EllipsedText inline>Visita en falso</EllipsedText>
                </StyledButton>
              )}
              {!jobIsClosed && (
                <StyledTooltip
                  content="Cancelar trabajo"
                  showArrow={false}
                >
                  <Button
                    size="mini"
                    shape="square"
                    onClick={openCancellationModal}
                    kind="tertiary"
                  >
                    <MaterialIcon name="cancel_presentation" />
                  </Button>
                </StyledTooltip>
              )}
              {job?.pdf && (
                <StyledTooltip
                  content={'Descargar PDF'}
                  showArrow={false}
                >
                  <Button
                    size="mini"
                    shape="square"
                    onClick={downloadFile}
                    kind="tertiary"
                  >
                    <MaterialIcon name="description" />
                  </Button>
                </StyledTooltip>
              )}
              {!editMode && (
                <>
                  {jobIsOpen && (
                    <StyledTooltip
                      content={'Editar cita'}
                      showArrow={false}
                    >
                      <Button
                        size="mini"
                        shape="square"
                        onClick={() => setEditMode(true)}
                        kind="tertiary"
                      >
                        <MaterialIcon name="edit" />
                      </Button>
                    </StyledTooltip>
                  )}
                  <StyledTooltip
                    content={'Actualizar'}
                    showArrow={false}
                  >
                    <Button
                      size="mini"
                      shape="square"
                      onClick={loadJob}
                      isLoading={loading}
                      kind="tertiary"
                    >
                      <MaterialIcon name="autorenew" />
                    </Button>
                  </StyledTooltip>
                </>
              )}
              {editMode && (
                <>
                  <StyledTooltip
                    content={'Cancelar'}
                    showArrow={false}
                  >
                    <Button
                      size="mini"
                      shape="square"
                      onClick={() => setEditMode(false)}
                      kind="tertiary"
                    >
                      <MaterialIcon name="cancel" />
                    </Button>
                  </StyledTooltip>
                  <StyledTooltip
                    content={'Guardar'}
                    showArrow={false}
                  >
                    <Button
                      size="mini"
                      shape="square"
                      onClick={onRescheduleSubmit}
                      kind="tertiary"
                      disabled={!canSubmit}
                    >
                      <MaterialIcon name="done" />
                    </Button>
                  </StyledTooltip>
                </>
              )}
            </FlexRow>
          </FlexColumn>
          <ConfirmModal
            description="¿Desea cancelar el trabajo?"
            isOpen={showCancellationModal}
            onConfirm={onCancelJob}
            onCancel={closeCancellationModal}
          />
          <JobFailedVisitModal
            isOpen={showFailedVisitModal}
            onClose={closeFailedVisitModal}
            jobId={jobId}
            onSubmitFinished={() => {
              loadJob();
              closeFailedVisitModal();
            }}
          />
        </FlexColumn>
      }
    />
  );
}
